// Tryb — derived dashboard values.
//
// The numbers and labels the studio shows on top of raw answers: certification
// progress, the category colour for an experience, the season metrics. Kept out
// components so the dashboard pages stay mostly markup.

import { BY_ID, CERTIFICATION, METRICS } from "@shared/fields.js";
import { empty, isVisible, validate } from "@shared/validation.js";

export const CERTIFICATION_TOTAL = 31;

const CATEGORY_COLOURS = {
  "Movement & fitness": "var(--sage-600)",
  "Food & drink": "var(--ember-600)",
  "Craft & making": "var(--honey-600)",
  Music: "var(--navy-700)",
  "Reading & writing": "var(--sage-700)",
  "Outdoors & nature": "var(--sage-500)",
  Wellbeing: "var(--navy-500)",
};

export function colourForCategory(cat) {
  return CATEGORY_COLOURS[cat] || "var(--navy-500)";
}

/** A certification promise counts only when it is answered AND valid. */
function isPromiseKept(id, vals) {
  const def = BY_ID[id];
  if (!def || !isVisible(def, vals) || validate(def, vals)) return false;
  const answered = def.type === "bool" || def.type === "boolfile" ? (vals[id] === true ? 1 : "") : vals[id];
  return !empty(answered);
}

export function certificationProgress(vals) {
  const ids = CERTIFICATION.flatMap((s) => s.ids);
  const done = ids.filter((id) => isPromiseKept(id, vals)).length;
  return { done, total: CERTIFICATION_TOTAL, pct: Math.round((done / CERTIFICATION_TOTAL) * 100) };
}

export function certificationGroups(vals, fieldViews) {
  return CERTIFICATION.map((s) => {
    const fields = fieldViews(s.ids, false);
    const done = s.ids.filter((id) => isPromiseKept(id, vals)).length;
    return { id: s.id, title: s.title, progress: `${done}/${fields.length}`, fields };
  });
}

export function certificationTier(done) {
  return done >= CERTIFICATION_TOTAL ? "Certified" : done >= 16 ? "Bronze" : "Not yet";
}

/**
 * Season metrics. Real numbers only exist once a season has actually run, so a
 * brand-new host sees em-dashes and the reason they are empty — never a zero
 * that reads like a bad score.
 */
export function seasonMetrics(stage, certDone) {
  const isNew = stage === "Brand-new";
  const isDone = stage === "Season 1 complete";
  const tier = certificationTier(certDone);

  const values = isNew ? ["—", "—", "—", tier] : isDone ? ["78%", "71%", "83%", tier] : ["—", "86%", "100%", tier];

  const subs = isNew
    ? ["Shows up after your first season", "Shows up after week two", "Shows up after your first night", `${certDone} of 31 done`]
    : isDone
      ? ["7 of 8 said they’d come back", "5 of 7 first-timers came back", "5 of 6 nights", `${certDone} of 31 done`]
      : ["After season one", "6 of 7 came back for week two", "1 of 1 nights so far", `${certDone} of 31 done`];

  return METRICS.map((m, i) => ({
    key: m.k,
    value: values[i],
    sub: subs[i],
    color: values[i] === "—" ? "var(--ink-400)" : "var(--text-primary)",
  }));
}

export function trybRow(t, reupPublished, iconForCategory) {
  // Review comes first: nothing below is true of a tryb that isn't on tryb.in yet.
  const inReview = t.review === "submitted";
  const sentBack = t.review === "rejected";

  const status = inReview
    ? "In review"
    : sentBack
      ? "Changes needed"
      : reupPublished || t.season === "reup"
    ? "Season 2 · members re-booking"
    : t.season === "complete"
      ? "Season 1 wrapped"
      : t.season === "running"
        ? "Season 1 · running"
        : "Live";

  const tone = inReview
    ? "warning"
    : sentBack
      ? "danger"
      : reupPublished || t.season === "reup"
    ? "brand"
    : t.season === "complete"
      ? "neutral"
      : t.season === "running"
        ? "success"
        : "info";

  return {
    id: t.id,
    title: t.title,
    cat: `${t.cat}${t.sub ? ` · ${t.sub}` : ""}`,
    meta: `${(t.day || "").slice(0, 3).toUpperCase()} · ${t.time || ""} · ${t.hood || ""}`,
    seats: t.joined ? `${t.joined} of ${t.total} joined · ${t.wait} waiting` : `0 of ${t.total} joined yet`,
    status,
    tone,
    color: colourForCategory(t.cat),
    icon: iconForCategory(t.cat),
    canReup: t.season === "complete" && !inReview && !sentBack,
    // What the facilitator should know about where it stands, in one line.
    note: inReview
      ? "With our team. It goes on tryb.in once someone has read it through."
      : sentBack
        ? t.feedback
        : "",
    sentBack,
  };
}
